import { cn } from '@/lib/helpers/utils';
import { useDidMountEffect } from '@/lib/hooks';
import { VariantProps } from 'class-variance-authority';
import { motion } from 'framer-motion';
import React from 'react';
import { Spinner, spinnerVariants } from './Spinner';

interface LoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  loaded: boolean;
  size?: VariantProps<typeof spinnerVariants>['size'];
}

const Loader = React.forwardRef<HTMLDivElement, LoaderProps>(({ loaded, children, className, size = 'sm' }, ref) => {
  let key: string = `loader_${loaded ? 'inactive' : 'active'}`;
  const [initialiseLoader, setInitialiseLoader] = React.useState<boolean>(false);

  useDidMountEffect(() => {
    setInitialiseLoader(loaded);
  }, [loaded]);

  return !loaded ? (
    <motion.div
      ref={ref}
      key={key}
      className={cn('w-full h-full flex items-center justify-center animate-pulse', className)}
      initial={{ opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 300, opacity: 0, transition: { ease: 'easeOut', duration: 0.75 } }}
    >
      <Spinner className="!m-0" size={size} />
    </motion.div>
  ) : (
    <motion.div
      ref={ref}
      key={key}
      initial={initialiseLoader && { opacity: 0 }}
      animate={initialiseLoader && { y: 0, opacity: 1 }}
      exit={initialiseLoader ? { y: 300, opacity: 0, transition: { ease: 'easeOut', duration: 0.75 } } : {}}
    >
      {children}
    </motion.div>
  );
});

export default React.memo(Loader);
